/**
 * Debug flags for data-flow diagnostics.
 * Full diagnostics: development only.
 * Production: minimal overhead, optionally enhanced via localStorage flag.
 */

const IS_DEV = typeof import.meta !== "undefined" && import.meta.env?.DEV === true;
const STORAGE_KEY = "studyDo_diagnostics";

type DebugFlags = {
  /** Enable structured flow diagnostics in console */
  flowDiagnostics: boolean;
  /** Run read-after-write consistency checks for critical flows */
  validateReadAfterWrite: boolean;
  /** Classify and log Supabase errors with root-cause hints */
  errorClassification: boolean;
  /** Trace query parameters before execution */
  traceQueryParams: boolean;
  /** Sample rate for expensive checks (0–1); 1 = always */
  consistencySampleRate: number;
  /** Log My Library fetch path (summary vs legacy), duration, count */
  libraryDiagnostics: boolean;
};

function parseStoredFlags(): Partial<DebugFlags> {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (!raw?.trim()) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      flowDiagnostics: Boolean(parsed.flowDiagnostics),
      validateReadAfterWrite: Boolean(parsed.validateReadAfterWrite),
      errorClassification: Boolean(parsed.errorClassification),
      traceQueryParams: Boolean(parsed.traceQueryParams),
      libraryDiagnostics: Boolean(parsed.libraryDiagnostics),
      consistencySampleRate:
        typeof parsed.consistencySampleRate === "number"
          ? Math.max(0, Math.min(1, parsed.consistencySampleRate))
          : 1,
    };
  } catch {
    return {};
  }
}

let cached: DebugFlags | null = null;

export function getDebugFlags(): DebugFlags {
  if (cached) return cached;
  const stored = parseStoredFlags();
  cached = {
    flowDiagnostics: IS_DEV || (stored.flowDiagnostics ?? false),
    validateReadAfterWrite: IS_DEV || (stored.validateReadAfterWrite ?? false),
    errorClassification: IS_DEV || (stored.errorClassification ?? true),
    traceQueryParams: stored.traceQueryParams ?? false,
    libraryDiagnostics: stored.libraryDiagnostics ?? false,
    consistencySampleRate: stored.consistencySampleRate ?? (IS_DEV ? 1 : 0.1),
  };
  return cached;
}

/** Reset cache (e.g. after localStorage change) */
export function resetDebugFlagsCache(): void {
  cached = null;
}

export function isDiagnosticsEnabled(): boolean {
  return getDebugFlags().flowDiagnostics || getDebugFlags().errorClassification;
}

export function shouldValidateReadAfterWrite(flow: string): boolean {
  const flags = getDebugFlags();
  if (!flags.validateReadAfterWrite) return false;
  if (flags.consistencySampleRate >= 1) return true;
  return Math.random() < flags.consistencySampleRate;
}
